<template>
      <div  style="margin: 60px; display: flex; flex-direction: row; justify-content: center;">
      <form action="#" @submit.prevent="login" v-if="showLoginForm">
          <h3>Entrar na {{ companyName }}</h3>

          <label for="username">Usuário</label>
          <input type="email" placeholder="E-mail" id="email" name="email" value required autofocus v-model="email">

          <label for="password">Senha</label>
          <input type="password" placeholder="Password" id="password" name="password" required v-model="password">

          <button @click="logLoginClick">Entrar</button>
          <div v-if="error" class="alert alert-danger">{{error}}</div>
          <button type="button" @click="resetPassword">Esqueci a senha</button>
          <div v-if="resetPasswordMessage" class="alert alert-info">{{ resetPasswordMessage }}</div>
      </form>

      <div v-else></div>

      <div v-if="showSuccessModal" class="modal">
        <div class="modal-content">
          <span class="close" @click="closeModal">&times;</span>
          <p>E-mail de redefinição de senha enviado. Verifique sua caixa de entrada.</p>
        </div>
      </div>

      <div v-if="showErrorModal" class="modal">
        <div class="modal-content">
          <span class="close" @click="closeModal">&times;</span>
          <p>Erro ao enviar e-mail de redefinição de senha: {{ resetPasswordMessage }}</p>
        </div>
      </div>
    </div>
  </template>
  
<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex' 
import { sendPasswordResetEmail, signInWithCustomToken, signOut } from "firebase/auth";
import { useRouter, useRoute } from 'vue-router'
import { auth } from '@/config/firebase';
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore'
import { checarVersaoFrontend } from "@/config/firebaseVersionCheck";
import { jwtDecode } from "jwt-decode";

export default {
    inheritAttrs:false,
    name: "LoginComponent",
    setup() {
    const email = ref('')
    const password = ref('')
    const error = ref(null)
    const resetPasswordMessage = ref(null)
    const showSuccessModal = ref(false);
    const showErrorModal = ref(false);
    const loggingIn = ref(false);
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const db = getFirestore()

    const companyName = computed(() => (route.params.company || '').toUpperCase());
    const normalizeSlug = (value) => (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const resolveAccountId = (company) => `acc_${normalizeSlug(company)}`;
    const legacyFallbackEnabled = String(process.env.VUE_APP_LOGIN_LEGACY_FALLBACK || 'off').toLowerCase() === 'on';
    const legacyFallbackAllowlist = new Set(
      String(process.env.VUE_APP_LOGIN_LEGACY_FALLBACK_COMPANIES || '')
        .split(',')
        .map((slug) => normalizeSlug(slug))
        .filter(Boolean)
    );
    const canUseLegacyLoginFallback = (company) => {
      if (!legacyFallbackEnabled) return false;
      const slug = normalizeSlug(company);
      if (!slug) return false;
      if (legacyFallbackAllowlist.has('*')) return true;
      return legacyFallbackAllowlist.has(slug);
    };
    const LOGIN_ACCESS_SOURCE_METRIC_KEY = 'iamPlatLoginAccessSourceV1';
    const trackLoginAccessSource = ({ source, company, accountId }) => {
      const sourceKey = (source || 'unknown').toString();
      const payload = {
        source: sourceKey,
        company: (company || '').toString(),
        accountId: (accountId || '').toString(),
        at: new Date().toISOString(),
      };
      try {
        const raw = localStorage.getItem(LOGIN_ACCESS_SOURCE_METRIC_KEY);
        const prev = raw ? JSON.parse(raw) : {};
        const bySource = (prev && prev.bySource) || {};
        const nextCount = Number(bySource[sourceKey] || 0) + 1;
        bySource[sourceKey] = nextCount;

        const next = {
          version: 1,
          updatedAt: payload.at,
          total: Number(prev.total || 0) + 1,
          bySource,
          last: payload,
        };
        localStorage.setItem(LOGIN_ACCESS_SOURCE_METRIC_KEY, JSON.stringify(next));
      } catch (_) {
        // ignore storage issues
      }
      try {
        window.dispatchEvent(new CustomEvent('iam:login-access-source', { detail: payload }));
      } catch (_) {
        // ignore event issues
      }
    };
    const buildAccessDecision = ({ approved, source, accountId, legacyRef, legacySnap }) => {
      trackLoginAccessSource({ source, company: route.params.company || '', accountId });
      return { approved, source, accountId, legacyRef, legacySnap };
    };

    const resolveUserAccess = async ({ company, email, uid }) => {
      const accountId = resolveAccountId(company);
      const emailLower = (email || '').toString().trim().toLowerCase();
      const uidValue = (uid || '').toString().trim();
      const safeGetDoc = async (path) => {
        try {
          return await getDoc(doc(db, path));
        } catch (err) {
          if ((err && err.code) === 'permission-denied') return null;
          throw err;
        }
      };

      // Fluxo canonico: sempre priorizar uid em account/member e module/user.
      if (uidValue) {
        const moduleUserSnap = await safeGetDoc(`accounts/${accountId}/modules/grc/users/${uidValue}`);
        if (moduleUserSnap && moduleUserSnap.exists()) {
          return buildAccessDecision({
            approved: moduleUserSnap.data()?.status !== 'inactive',
            source: 'accounts_module_user_uid',
            accountId,
          });
        }
      }

      if (uidValue) {
        const memberSnap = await safeGetDoc(`accounts/${accountId}/members/${uidValue}`);
        if (memberSnap && memberSnap.exists()) {
          return buildAccessDecision({
            approved: memberSnap.data()?.status !== 'inactive',
            source: 'accounts_member_uid',
            accountId,
          });
        }
      }

      if (!canUseLegacyLoginFallback(company)) {
        return buildAccessDecision({
          approved: false,
          source: 'accounts_only',
          accountId,
        });
      }

      // Compatibilidade transitória aprovada: fallback legado somente quando flag + allowlist estiverem ativos.
      const legacyUserRef = doc(db, `cliente/${company}/users/${emailLower}`);
      const legacyUserSnap = await getDoc(legacyUserRef);
      return buildAccessDecision({
        approved: legacyUserSnap.exists() && legacyUserSnap.data()?.approved === true,
        source: 'legacy_cliente_user',
        accountId,
        legacyRef: legacyUserRef,
        legacySnap: legacyUserSnap,
      });
    };
    const hasSsoToken = computed(() => Boolean(route.query.token));
    const ssoPaused = computed(() => Boolean(route.query && (route.query.sso === '0' || route.query.sso === 'false')));
    const forceSso = computed(() => Boolean(route.query && (route.query.sso === '1' || route.query.sso === 'true')));
    const hasSsoConfigured = computed(() => Boolean(route.params.company) && route.meta?.ssoEnabled);
    const isLocalHost = computed(() => {
      const host = (window.location.hostname || '').toLowerCase();
      return host === 'localhost' || host === '127.0.0.1';
    });
    const shouldRedirectToSSO = computed(() => {
      if (!hasSsoConfigured.value || hasSsoToken.value || ssoPaused.value) return false;
      // Em localhost evitamos auto-redirecionamento para SSO para não causar loop durante desenvolvimento.
      if (isLocalHost.value && !forceSso.value) return false;
      return true;
    });
    const ssoStateResolved = ref(false);
    const ssoProcessing = ref(false);
    const showSsoMessage = computed(() => ssoStateResolved.value && shouldRedirectToSSO.value && !ssoProcessing.value);
    const showLoginForm = computed(() => ssoStateResolved.value && !shouldRedirectToSSO.value && !ssoProcessing.value);
    const clearLoginArtifacts = () => {
      ['sessionExpiresAtMs', 'justLoggedInWithSSO', 'userEmail'].forEach((key) => {
        localStorage.removeItem(key);
      });
    };

    const redirectToSSO = () => {
      const basePath = window.location.pathname.replace(/\/entrar$/, '');
      const envRedirectBase = (process.env.VUE_APP_SSO_REDIRECT_OVERRIDE || '').toString().trim();
      const normalizedOverride = envRedirectBase ? envRedirectBase.replace(/\/$/, '') : '';

      // Seguranca: ignora override local quando o host atual nao e local.
      const currentHost = (window.location.hostname || '').toLowerCase();
      const isLocalHost = currentHost === 'localhost' || currentHost === '127.0.0.1';
      let useOverride = false;
      if (normalizedOverride) {
        try {
          const overrideHost = new URL(normalizedOverride).hostname.toLowerCase();
          useOverride = isLocalHost || overrideHost === currentHost;
        } catch (_) {
          useOverride = false;
        }
      }

      const targetUrl = useOverride
        ? `${normalizedOverride}${basePath}`
        : window.location.href;
      const baseSsoUrl = (process.env.VUE_APP_SSO_FUNCTION_URL || '').toString().trim()
        || 'https://southamerica-east1-due-prd.cloudfunctions.net/loginWithSSO';
      const slug = (route.params.company || '').toString().trim();
      const redirectURL = `${baseSsoUrl}?redirect=${encodeURIComponent(targetUrl)}${slug ? `&company=${encodeURIComponent(slug)}` : ''}`;
      window.location.href = redirectURL;
    };

    const logLoginClick = () => {};

    const waitForFirebaseSession = async (timeoutMs = 5000) => {
      const startedAt = Date.now();
      while (Date.now() - startedAt < timeoutMs) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            await currentUser.getIdToken();
            return currentUser;
          } catch (_) {
            // segue tentando até o timeout
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      return null;
    };

    const login = async () => {
      loggingIn.value = true;
      try {
        const currentCompany = route.params.company.toLowerCase()
        await store.dispatch('logIn', {
          email: email.value,
          password: password.value
        });
        const authUser = await waitForFirebaseSession();
        if (!authUser) {
          await signOut(auth).catch(() => {});
          await store.dispatch('logOut');
          throw new Error('Não foi possível estabelecer sessão Firebase estável no login.');
        }

        const userExists = await checkUserCompany(email.value, currentCompany)
        if (!userExists) {
          await signOut(auth).catch(() => {});
          await store.dispatch('logOut');
          throw new Error('Usuário sem acesso à plataforma');
        }

        const expiresAtMs = Date.now() + 8 * 60 * 60 * 1000;
        localStorage.setItem('sessionExpiresAtMs', String(expiresAtMs));
        localStorage.setItem('lastCliente', currentCompany);
        router.push(`/${currentCompany}`);
      } catch (err) {
        error.value = err.message
      } finally {
        loggingIn.value = false;
      }
    }

    const checkUserCompany = async (emailValue, company) => {
      const access = await resolveUserAccess({
        company,
        email: emailValue,
        uid: auth.currentUser?.uid || '',
      });
      return access.approved === true;
    }

    const resetPassword = async () => {
      if (!email.value) {
        resetPasswordMessage.value = 'Por favor, insira seu e-mail para resetar a senha.'
        return
      }
      try {
        await sendPasswordResetEmail(auth, email.value)
        resetPasswordMessage.value = ''
      showSuccessModal.value = true;
      } catch (err) {
        resetPasswordMessage.value = err.message || 'Erro desconhecido'
        showErrorModal.value = true;
      }
    }

    const closeModal = () => {
      showSuccessModal.value = false;
      showErrorModal.value = false;
    };

    const finalizeSSO = async (company, emailLogado, user) => {
      const access = await resolveUserAccess({
        company,
        email: emailLogado,
        uid: user?.uid || auth.currentUser?.uid || '',
      });
      if (!access.approved) {
        error.value = "Usuário sem acesso à plataforma.";
        return;
      }

      const userDocRef = access.legacyRef || doc(db, `accounts/${access.accountId}/users/${emailLogado}`);
      const userDoc = access.legacySnap || await getDoc(userDocRef);

      const storedUser = user
        ? { ...user }
        : {
            email: emailLogado,
            displayName: userDoc.data()?.name || '',
            photoURL: userDoc.data()?.avatarUrl || '',
          };
      if (!storedUser.photoURL && userDoc.data()?.avatarUrl) {
        storedUser.photoURL = userDoc.data().avatarUrl;
      }
      const complianceName = storedUser.displayName || userDoc.data()?.name || '';
      try {
        await setDoc(userDocRef, {
          name: complianceName,
          avatarUrl: storedUser.photoURL || '',
        }, { merge: true });
      } catch (_) {
        /* ignore */
      }
      store.commit("SET_USER", storedUser);
      store.commit("SET_EMAIL", emailLogado);
      const expiresAtMs = Date.now() + 8 * 60 * 60 * 1000;
      localStorage.setItem('sessionExpiresAtMs', String(expiresAtMs));
      localStorage.setItem('justLoggedInWithSSO', JSON.stringify({ slug: company, expiresAt: Date.now() + 15000 }));
      localStorage.setItem("userEmail", emailLogado);
      localStorage.setItem('lastCliente', company);
      await store.dispatch('setCompany', company);
      const targetPath = `/${company}`;
      router.replace({ path: targetPath }).catch(() => { /* ignore */ });
    };

    const handleSSOToken = async (token) => {
      ssoProcessing.value = true;
      const currentCompany = route.params.company?.toLowerCase();
      if (!currentCompany) {
        error.value = "Empresa não identificada na rota.";
        return;
      }

      let emailLogado = "";
      try {
        const credential = await signInWithCustomToken(auth, token);
        if (credential?.user?.email) {
          emailLogado = credential.user.email.toLowerCase();
        } else if (credential?.user?.uid) {
          emailLogado = credential.user.uid.toLowerCase();
        }
      if (emailLogado) {
        await finalizeSSO(currentCompany, emailLogado, credential.user);
        return;
      }
      } catch (_) {
        /* ignore */
      }

      try {
        const decoded = jwtDecode(token);
        emailLogado = (decoded?.uid || decoded?.email || "").toString().toLowerCase();
      } catch (_) {
        /* ignore */
      }

      if (!emailLogado) {
        error.value = "Não foi possível identificar o usuário do token SSO.";
        ssoProcessing.value = false;
        return;
      }
      await finalizeSSO(currentCompany, emailLogado, null);
      ssoProcessing.value = false;
    };

    onMounted(async () => {
      const currentCompany = (route.params.company || '').toString().toLowerCase();
      const token = route.query.token;
      const hasActiveSession = Boolean(auth.currentUser);

      if (token) {
        await signOut(auth).catch(() => {});
        clearLoginArtifacts();
        localStorage.removeItem("ssoRedirectBlocked");
      } else if (!hasActiveSession) {
        clearLoginArtifacts();
        localStorage.removeItem("ssoRedirectBlocked");
      } else if (currentCompany) {
        // Evita destruir sessão válida quando a rota de login é aberta por redirecionamento transitório.
        store.commit("SET_USER", auth.currentUser);
        if (auth.currentUser.email) {
          store.commit("SET_EMAIL", auth.currentUser.email.toLowerCase());
        }
        await store.dispatch('setCompany', currentCompany);
        router.replace({ path: `/${currentCompany}` }).catch(() => {});
        return;
      }

      await checarVersaoFrontend();
      if (!token && shouldRedirectToSSO.value) {
        redirectToSSO();
        return;
      }
      ssoStateResolved.value = true;
      if (!token) return;
      await handleSSOToken(token);
    });

    return { email, password, error, resetPasswordMessage, login, resetPassword, closeModal, companyName, redirectToSSO, logLoginClick, showSuccessModal, showErrorModal, loggingIn, showLoginForm, showSsoMessage, ssoPaused }
  }
}
</script>


<style scoped>
  *,
  *:before,
  *:after{
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  }
  body{
  background-color: #080710;
  }
  .background{
  width: 430px;
  height: 620px;
  position: absolute;
  transform: translate(-50%,-50%);
  left: 50%;
  top: 50%;
  }
  .background .shape{
  height: 200px;
  width: 200px;
  position: absolute;
  border-radius: 50%;
  }
  .shape:first-child{
  background: linear-gradient(
    #1845ad,
    #23a2f6
  );
  left: -80px;
  top: -80px;
  }
  .shape:last-child{
  background: linear-gradient(
    to right,
    #035762,
    #09bfd7
  );
  right: -30px;
  bottom: -80px;
  }
  form{
  height: 620px;
  width: 400px;
  background-color: rgba(255,255,255,0.13);
  position: relative;
  /* transform: translate(-50%,-50%); */
  /* top: 50%;
  left: 50%; */
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255,255,255,0.1);
  box-shadow: 8px 8px 10px 8px #BACCDE8A;
  padding: 50px 35px;
  }
  form *{
  font-family: 'Poppins',sans-serif;
  color: #191e23;
  letter-spacing: 0.5px;
  outline: none;
  border: none;
  }
  form h3{
  font-size: 32px;
  font-weight: 500;
  line-height: 42px;
  text-align: center;
  }
  
  label{
  display: block;
  margin-top: 30px;
  font-size: 16px;
  font-weight: 500;
  }
  input{
  display: block;
  height: 50px;
  width: 100%;
  background-color: rgba(255,255,255);
  border-radius: 3px;
  padding: 0 10px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 300;
  }
  ::placeholder{
  color: #191e23;
  }
  button{
  margin-top: 50px;
  width: 100%;
  background-color: #191e23;
  color: white;
  padding: 15px 0;
  font-size: 18px;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
  }
  .social{
  margin-top: 30px;
  display: flex;
  }
  .social div{
  background: red;
  width: 150px;
  border-radius: 3px;
  padding: 5px 10px 10px 5px;
  background-color: rgba(255,255,255,0.27);
  color: #eaf0fb;
  text-align: center;
  }
  .social div:hover{
  background-color: rgba(255,255,255,0.47);
  }
  .social .fb{
  margin-left: 25px;
  }
  .social i{
  margin-right: 4px;
  }
  
.modal {
  display: block; 
  position: fixed; 
  z-index: 1; 
  left: 0;
  top: 0;
  width: 100%; 
  height: 100%; 
  overflow: auto; 
  background-color: rgb(0,0,0); 
  background-color: rgba(0,0,0,0.4);
}

.modal-content {
  background-color: #fefefe;
  margin: 15% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 80%; 
}

.close {
  color: #aaa;
  float: right;
  font-size: 28px;
  font-weight: bold;
}

.close:hover,
.close:focus {
  color: black;
  text-decoration: none;
  cursor: pointer;
}
</style>

